'use client';
import React from 'react';
import { useRouter } from 'next/navigation';

const Security: React.FC = () => {
  const router = useRouter();

  return (
    <section className="min-h-[70vh] py-20 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="glass-card rounded-[40px] p-8 md:p-12 border-[var(--color-border)] shadow-2xl">
          <p className="text-[var(--color-primary)] text-xs font-bold uppercase tracking-[0.3em]">Security & Privacy</p>
          <h1 className="text-3xl md:text-4xl font-black text-[var(--color-text)] mt-4">Security and privacy built for tutoring centers</h1>
          <p className="text-[var(--color-muted)] text-lg mt-4 leading-relaxed font-medium">
            We protect student and educator data with clear controls, strong safeguards, and transparent practices.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 text-xs font-semibold text-[var(--color-muted)]">
            <span className="badge">Last updated: February 11, 2026</span>
            <span className="badge">Applies to Intelgrader web and associated services</span>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card">
              <h2 className="text-lg font-bold text-[var(--color-text)]">Purpose</h2>
              <p className="text-[var(--color-muted)] mt-3">
                This page explains how Intelgrader collects, uses, shares, and safeguards information when you use our
                products and services. We want you to understand what data we handle, why we handle it, and the
                protections in place.
              </p>
            </div>

            <div className="card">
              <h2 className="text-lg font-bold text-[var(--color-text)]">How we use information</h2>
              <ul className="mt-3 space-y-2 text-[var(--color-muted)] text-sm leading-relaxed">
                <li>Deliver core product features (grading, feedback, reports, and insights).</li>
                <li>Support account setup, authentication, and secure access.</li>
                <li>Monitor system performance and prevent misuse or fraud.</li>
                <li>Improve product accuracy, usability, and reliability.</li>
              </ul>
            </div>

            <div className="card">
              <h2 className="text-lg font-bold text-[var(--color-text)]">What we collect and why</h2>
              <div className="mt-3 space-y-4 text-sm text-[var(--color-muted)]">
                <div>
                  <p className="font-semibold text-[var(--color-text)]">Information provided by institutions</p>
                  <ul className="mt-2 space-y-2">
                    <li>Account details (name, institutional email, role, and class or cohort identifiers).</li>
                    <li>Enrollment and subject context needed to deliver grading and reporting.</li>
                    <li>Optional, anonymized analytics provided for evaluation or benchmarking.</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-[var(--color-text)]">Information from direct use</p>
                  <ul className="mt-2 space-y-2">
                    <li>Usage signals (feature access, clicks, and error logs) to improve reliability.</li>
                    <li>Device and network details (IP address, browser, OS) to keep accounts secure.</li>
                    <li>Uploaded work and outputs (submissions, feedback, and reports).</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card">
              <h2 className="text-lg font-bold text-[var(--color-text)]">Information security</h2>
              <p className="text-[var(--color-muted)] mt-3">
                We use layered security controls to protect data in transit and at rest.
              </p>
              <ul className="mt-3 space-y-2 text-[var(--color-muted)] text-sm leading-relaxed">
                <li>Encryption in transit (TLS/SSL) for all communications.</li>
                <li>Role-based access controls and least-privilege access.</li>
                <li>Audit logging and regular monitoring for suspicious activity.</li>
                <li>Secure backups and disaster recovery practices.</li>
              </ul>
            </div>

            <div className="card">
              <h2 className="text-lg font-bold text-[var(--color-text)]">Sharing and service providers</h2>
              <p className="text-[var(--color-muted)] mt-3">
                We share data only as needed to deliver the service. This may include trusted infrastructure and analytics providers.
              </p>
              <ul className="mt-3 space-y-2 text-[var(--color-muted)] text-sm leading-relaxed">
                <li>Hosting and storage providers for secure data handling.</li>
                <li>Support and monitoring tools used to maintain reliability.</li>
                <li>Institutional access consistent with your institution’s policies.</li>
              </ul>
            </div>

            <div className="card">
              <h2 className="text-lg font-bold text-[var(--color-text)]">Retention</h2>
              <p className="text-[var(--color-muted)] mt-3">
                We retain data only as long as necessary to provide the service or meet legal requirements. When no longer needed,
                data is securely deleted.
              </p>
              <div className="mt-4 p-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-alt)] text-sm text-[var(--color-muted)]">
                <p className="font-semibold text-[var(--color-text)]">Data deletion requests</p>
                <p className="mt-1">Email: learnline@learnline.in</p>
              </div>
            </div>

            <div className="card">
              <h2 className="text-lg font-bold text-[var(--color-text)]">Your rights</h2>
              <ul className="mt-3 space-y-2 text-[var(--color-muted)] text-sm leading-relaxed">
                <li>Access: Request a copy of the personal data we hold.</li>
                <li>Correction: Ask us to update inaccurate information.</li>
                <li>Deletion: Request deletion where permitted by law.</li>
              </ul>
            </div>

            <div className="card">
              <h2 className="text-lg font-bold text-[var(--color-text)]">Incident response</h2>
              <p className="text-[var(--color-muted)] mt-3">
                In the event of a security incident affecting personal data, we will notify impacted customers in a timely manner,
                in line with applicable laws and contractual obligations.
              </p>
            </div>

            <div className="card">
              <h2 className="text-lg font-bold text-[var(--color-text)]">Support and contact</h2>
              <p className="text-[var(--color-muted)] mt-3">
                If you have questions about security or privacy, contact our team.
              </p>
              <div className="mt-4 p-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-alt)] text-sm text-[var(--color-muted)]">
                <p>Email: learnline@learnline.in</p>
                <p className="mt-1">Phone: +91-9475539473</p>
              </div>
            </div>

            <div className="card md:col-span-2">
              <h2 className="text-lg font-bold text-[var(--color-text)]">Changes to this page</h2>
              <p className="text-[var(--color-muted)] mt-3">
                We may update this page to reflect product or legal changes. We will post the latest version here.
              </p>
            </div>
          </div>

          <button
            onClick={() => router.push('/')}
            className="btn btn-primary mt-10"
          >
            Back to Home
          </button>
        </div>
      </div>
    </section>
  );
};

export default Security;
